import React, { useMemo } from 'react';
import { getPacNombre } from '../../utils/citasHelpers';

const ESTADO_CITA = {
  pendiente: { label: 'Pendiente', pill: 'warning', icon: 'bi-clock', stripe: 'pendiente' },
  confirmada: { label: 'Confirmada', pill: 'primary', icon: 'bi-check-circle', stripe: 'confirmada' },
  realizada: { label: 'Realizada', pill: 'success', icon: 'bi-check2-all', stripe: 'realizada' },
  cancelada: { label: 'Cancelada', pill: 'secondary', icon: 'bi-x-circle', stripe: 'cancelada' },
  reagendada: { label: 'Reagendada', pill: 'info', icon: 'bi-arrow-repeat', stripe: 'reagendada' },
};

function formatCitaFecha(iso) {
  try {
    const d = new Date(iso);
    return {
      dia: d.toLocaleDateString('es-EC', { day: '2-digit' }),
      mes: d.toLocaleDateString('es-EC', { month: 'short' }).replace('.', ''),
      hora: d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
      diaSemana: d.toLocaleDateString('es-EC', { weekday: 'short' }),
      completa: d.toLocaleString('es-EC', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
      ts: d.getTime(),
    };
  } catch {
    return { dia: '—', mes: '', hora: '', diaSemana: '', completa: String(iso || '—'), ts: 0 };
  }
}

function CitaCard({
  cita,
  destacada = false,
  highlighted = false,
  onEditar,
  onCancelar,
  onConfirmar,
  onMarcarRealizada,
  onVerDetalle,
  pacienteNombre = '',
  compact = false,
}) {
  const f = formatCitaFecha(cita.fechaHora);
  const est = ESTADO_CITA[cita.estado] || {
    label: cita.estado || 'Sin estado',
    pill: 'secondary',
    icon: 'bi-dash-circle',
    stripe: 'default',
  };
  const activa = cita.estado !== 'cancelada' && cita.estado !== 'realizada';

  return (
    <article
      className={[
        'pd-cita-card',
        `pd-cita-card--${est.stripe}`,
        destacada ? 'pd-cita-card--next' : '',
        highlighted ? 'pd-cita-card--focused' : '',
        onVerDetalle ? 'pd-cita-card--clickable' : '',
        compact ? 'pd-cita-card--compact' : '',
      ].filter(Boolean).join(' ')}
      role={onVerDetalle ? 'button' : undefined}
      tabIndex={onVerDetalle ? 0 : undefined}
      onClick={onVerDetalle ? () => onVerDetalle(cita) : undefined}
      onKeyDown={
        onVerDetalle
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onVerDetalle(cita);
              }
            }
          : undefined
      }
    >
      <div className="pd-cita-card__fecha" aria-hidden>
        <span className="pd-cita-card__dia-sem">{f.diaSemana}</span>
        <span className="pd-cita-card__dia">{f.dia}</span>
        <span className="pd-cita-card__mes">{f.mes}</span>
        <span className="pd-cita-card__hora">{f.hora}</span>
      </div>

      <div className="pd-cita-card__body">
        <div className="pd-cita-card__top">
          <time className="pd-cita-card__when" dateTime={cita.fechaHora}>
            {f.completa}
          </time>
          <span className={`pd-cita-estado pd-cita-estado--${est.pill}`}>
            <i className={`bi ${est.icon}`} aria-hidden />
            {est.label}
          </span>
        </div>

        {pacienteNombre ? (
          <p className="pd-cita-card__paciente">
            <span className="pd-cita-card__paciente-avatar" aria-hidden>
              {(pacienteNombre[0] || 'P').toUpperCase()}
            </span>
            {pacienteNombre}
          </p>
        ) : null}

        <p className="pd-cita-card__motivo">
          {cita.motivo?.trim() || 'Consulta odontológica'}
        </p>

        {destacada ? (
          <span className="pd-cita-card__tag">
            <i className="bi bi-star-fill" aria-hidden />
            Próxima
          </span>
        ) : null}

        <div className="pd-cita-card__actions" onClick={(e) => e.stopPropagation()} role="presentation">
          {cita.estado === 'pendiente' && onConfirmar && (
            <button
              type="button"
              className="pd-cita-action pd-cita-action--confirm"
              onClick={() => onConfirmar(cita)}
              title="Confirmar cita"
            >
              <i className="bi bi-check-lg" />
              <span>Confirmar</span>
            </button>
          )}
          {onEditar && (
            <button
              type="button"
              className="pd-cita-action pd-cita-action--edit"
              onClick={() => onEditar(cita)}
              title={activa ? 'Mover u otro horario' : 'Ver cita'}
            >
              <i className={`bi ${activa ? 'bi-arrows-move' : 'bi-eye'}`} />
              <span>{activa ? 'Mover' : 'Ver'}</span>
            </button>
          )}
          {cita.estado === 'confirmada' && onMarcarRealizada && (
            <button
              type="button"
              className="pd-cita-action pd-cita-action--done"
              onClick={() => onMarcarRealizada(cita)}
              title="Marcar realizada"
            >
              <i className="bi bi-clipboard2-check" />
              <span>Realizada</span>
            </button>
          )}
          {activa && onCancelar && (
            <button
              type="button"
              className="pd-cita-action pd-cita-action--cancel"
              onClick={() => onCancelar(cita)}
              title="Cancelar cita"
            >
              <i className="bi bi-x-lg" />
              <span>Cancelar</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function PacienteCitasList({
  citas = [],
  maxVisible = 12,
  variant = 'default',
  onAgendar,
  onEditar,
  onCancelar,
  onConfirmar,
  onMarcarRealizada,
  onVerDetalle,
  highlightCitaId = null,
  pacientes = null,
  emptyMessage,
  hideDrawerCount = false,
}) {
  const isDrawer = variant === 'drawer';

  const ordenadas = useMemo(() => {
    const list = [...citas];
    list.sort((a, b) => {
      const diff = new Date(a.fechaHora) - new Date(b.fechaHora);
      return isDrawer ? diff : -diff;
    });
    return list;
  }, [citas, isDrawer]);

  const ahora = Date.now();
  const proximaId = useMemo(() => {
    const futuras = ordenadas
      .filter((c) => c.estado !== 'cancelada' && formatCitaFecha(c.fechaHora).ts >= ahora)
      .sort((a, b) => formatCitaFecha(a.fechaHora).ts - formatCitaFecha(b.fechaHora).ts);
    return futuras[0]?.id ?? null;
  }, [ordenadas, ahora]);

  const visibles = ordenadas.slice(0, maxVisible);
  const restantes = ordenadas.length - maxVisible;

  if (ordenadas.length === 0) {
    return (
      <div className={`pd-citas-empty${isDrawer ? ' pd-citas-empty--drawer' : ''}`}>
        <div className="pd-citas-empty__icon">
          <i className="bi bi-calendar2-check" aria-hidden />
        </div>
        <h4 className="pd-citas-empty__title">Sin citas aquí</h4>
        <p className="pd-citas-empty__desc">
          {emptyMessage || 'Agendá una cita desde el botón de arriba o el botón flotante.'}
        </p>
        {onAgendar ? (
          <button type="button" className="pd-citas-empty__btn" onClick={onAgendar}>
            <i className="bi bi-calendar-plus" />
            Agendar cita
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`pd-citas-panel${isDrawer ? ' pd-citas-panel--drawer' : ''}`}>
      {!isDrawer ? (
        <div className="pd-citas-panel__head">
          <p className="pd-citas-panel__summary">
            <i className="bi bi-calendar-check" aria-hidden />
            {ordenadas.length} cita{ordenadas.length !== 1 ? 's' : ''}
            {proximaId ? ' · próxima resaltada' : ''}
          </p>
          {onAgendar ? (
            <button type="button" className="cp-btn cp-btn--primary cp-btn--sm pd-citas-panel__link" onClick={onAgendar}>
              <i className="bi bi-calendar-plus" />
              Nueva cita
            </button>
          ) : null}
        </div>
      ) : hideDrawerCount ? null : (
        <p className="pd-citas-panel__drawer-count">
          <span className="pd-citas-panel__drawer-count-num">{ordenadas.length}</span>
          {ordenadas.length === 1 ? 'cita en esta vista' : 'citas en esta vista'}
        </p>
      )}

      <ul className="pd-citas-list">
        {visibles.map((c) => (
          <li key={c.id}>
            <CitaCard
              cita={c}
              compact={isDrawer}
              destacada={String(c.id) === String(proximaId)}
              highlighted={highlightCitaId != null && String(c.id) === String(highlightCitaId)}
              pacienteNombre={pacientes ? getPacNombre(pacientes, c.idPaciente) : ''}
              onEditar={onEditar}
              onCancelar={onCancelar}
              onConfirmar={onConfirmar}
              onMarcarRealizada={onMarcarRealizada}
              onVerDetalle={onVerDetalle}
            />
          </li>
        ))}
      </ul>

      {restantes > 0 ? (
        <div className="pd-citas-panel__footer">
          <p className="pd-citas-panel__footer-text">+{restantes} más en el listado</p>
        </div>
      ) : null}
    </div>
  );
}
